import type { ReactElement } from 'react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { API_ORIGIN } from '../api/client';
import { Button, Card, Empty } from '../ui';

/** What each kind is called to the person receiving it. */
const KINDS: Record<string, string> = {
  noteReady: 'when a meeting you were in has been written up',
  shared: 'when somebody shares a meeting with you',
  weekly: 'the Monday summary of what you owe and are owed',
  beforeMeeting: 'the note half an hour before a meeting',
};

/**
 * The page at the end of the stop link in every message (PRD 41).
 *
 * ⚠ Open to anybody with the link, and deliberately so: somebody who wants
 * these to stop is usually on a phone, signed into nothing, and being told to
 * sign in first is how a stop link becomes a spam complaint. The token in the
 * URL is the whole of the authority, and it can only ever turn something off.
 *
 * ⚠ And a button, never an automatic POST on load. Several mail clients
 * prefetch the links in a message; a page that acts on arrival would
 * unsubscribe people who never clicked anything.
 */
export function StopMail(): ReactElement {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const kind = params.get('kind') ?? '';
  const [state, setState] = useState<'asking' | 'working' | 'done' | 'failed'>('asking');

  const what = KINDS[kind];
  if (!token || !what)
    return (
      <div className="page stack-lg">
        <Card flush>
          <Empty lead="This link is incomplete.">
            Open the message again and press the link at the bottom of it — or sign in and change
            what you are written to about in <Link to="/settings">Settings</Link>.
          </Empty>
        </Card>
      </div>
    );

  const stop = async () => {
    setState('working');
    try {
      const res = await fetch(`${API_ORIGIN}/v1/mail/stop`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'x-ptitim-web': '1' },
        body: JSON.stringify({ token, kind }),
      });
      setState(res.ok ? 'done' : 'failed');
    } catch {
      setState('failed');
    }
  };

  return (
    <div className="page stack-lg" style={{ maxWidth: 560 }}>
      <Card title={state === 'done' ? 'Done.' : 'Stop these messages?'} flush>
        <div className="stack" style={{ padding: '16px 20px', gap: 14 }}>
          {state === 'done' ? (
            <>
              <span className="t-body">
                You will not be written to {what} again. Nothing else changes — the other kinds
                carry on, and everything is still in the app.
              </span>
              <span className="t-secondary">
                Changed your mind? Sign in and turn it back on in{' '}
                <Link to="/settings">Settings</Link>.
              </span>
            </>
          ) : (
            <>
              <span className="t-body">Ptitim will stop writing to you {what}.</span>
              <span className="t-secondary">
                This is the only kind it stops. You can change all four in Settings once you are
                signed in, and nothing here touches your meetings.
              </span>
              <div className="cluster">
                <Button
                  variant="primary"
                  onClick={() => void stop()}
                  working={state === 'working' ? 'Stopping…' : false}
                >
                  Stop these
                </Button>
                <Link to="/" className="btn">
                  Keep them
                </Link>
              </div>
              {state === 'failed' ? (
                <span className="t-secondary">
                  That did not go through. Try again, or change it in Settings.
                </span>
              ) : null}
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
